import { useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { HubButton, Screen } from '../components/Screen'
import { EmptyState, ErrorState, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { EVENT_KIND_LABEL, eventsSoon, formatEventWhen, pluralParticipants, relativeStart } from '../data/events'
import { useEvents, usePrefetchBoards, useSessions } from '../data/hooks'
import { formatDayLong, formatTimeRange, isoDate, planToday, pluralAthletes } from '../data/schedule'
import type { SessionSlot } from '../data/types'

const MONTH_NOM_LOWER = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
]

/**
 * S03 «Сегодня»: рабочий день начинается с тренировок. Главный шаг -
 * открыть ближайшее занятие; лимиты тарифа и лента первый экран не занимают.
 */
export function TodayScreen() {
  const { now } = useEnv()
  const navigate = useNavigate()
  const today = now()
  const day = isoDate(today)
  const sessions = useSessions(day, day)
  const plan = sessions.data ? planToday(sessions.data, today) : null

  // В зале может не быть сети: доски всех сегодняшних занятий подгружаем
  // заранее, ближайшие первыми (PWA, этап 4).
  const boardKeys = plan ? [plan.focus, ...plan.later, ...plan.done].flatMap((s) => (s ? [s.key] : [])) : []
  usePrefetchBoards(boardKeys, sessions.isSuccess && !sessions.isFetching)

  const open = (slot: SessionSlot) => navigate(`/sessions/${encodeURIComponent(slot.key)}`)

  let body
  if (sessions.isPending) {
    body = <Skeleton rows={2} label="Загружаю занятия на сегодня" />
  } else if (sessions.isError) {
    body = <ErrorState title="Не удалось загрузить занятия" onRetry={() => void sessions.refetch()} />
  } else if (plan) {
    const nothingToday = sessions.data.length === 0
    body = (
      <>
        {plan.focus ? (
          <section className="pc-card" style={{ marginTop: 16 }} aria-labelledby="pc-focus-title">
            <span className="pc-badge">{plan.focusState === 'now' ? 'Идёт сейчас' : 'Ближайшая'}</span>
            <h2 id="pc-focus-title" className="pc-slot__title" style={{ fontSize: 26, marginTop: 10 }}>
              {plan.focus.groupName}
            </h2>
            <p className="pc-muted" style={{ margin: '4px 0 14px' }}>
              {formatTimeRange(plan.focus)} / {pluralAthletes(plan.focus.athleteCount)}
            </p>
            <button type="button" className="pc-btn pc-btn--primary pc-btn--block" onClick={() => open(plan.focus!)}>
              Открыть тренировку
            </button>
          </section>
        ) : nothingToday ? (
          <EmptyState
            title="На сегодня занятий нет"
            text={formatDayLong(today)}
            action={
              <button type="button" className="pc-btn" onClick={() => navigate('/schedule')}>
                Открыть расписание
              </button>
            }
          />
        ) : (
          <EmptyState
            title="Занятия на сегодня закончились"
            text="Посещаемость прошедших занятий можно проверить ниже."
          />
        )}

        {plan.later.length > 0 && (
          <section className="pc-section" aria-labelledby="pc-later">
            <h2 id="pc-later" className="pc-section-title">
              Следом
            </h2>
            <SlotList slots={plan.later} onOpen={open} />
          </section>
        )}

        {plan.done.length > 0 && (
          <section className="pc-section" aria-labelledby="pc-done">
            <h2 id="pc-done" className="pc-section-title">
              Уже прошли
            </h2>
            <SlotList slots={plan.done} onOpen={open} />
          </section>
        )}
      </>
    )
  }

  return (
    <Screen title="Сегодня" subtitle={formatDayLong(today)}>
      {body}
      <EventsSoon />
      <section className="pc-section" aria-label="Быстрые переходы" style={{ display: 'grid', gap: 8 }}>
        <button type="button" className="pc-btn pc-btn--block pc-btn--row" onClick={() => navigate('/payments')}>
          Оплаты за {MONTH_NOM_LOWER[today.getMonth()]}
          <ChevronRight size={20} aria-hidden="true" />
        </button>
        <button type="button" className="pc-btn pc-btn--block pc-btn--row" onClick={() => navigate('/schedule/events')}>
          Соревнования и сборы
          <ChevronRight size={20} aria-hidden="true" />
        </button>
        <HubButton inline />
      </section>
    </Screen>
  )
}

function SlotList({ slots, onOpen }: { slots: SessionSlot[]; onOpen: (s: SessionSlot) => void }) {
  return (
    <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
      {slots.map((s) => (
        <li key={s.key}>
          <button type="button" className="pc-list-row" onClick={() => onOpen(s)}>
            <span className="pc-list-row__main">
              <span className="pc-list-row__title" style={{ display: 'block' }}>
                {s.groupName}
              </span>
              <span className="pc-list-row__meta">
                {s.cancelled ? 'Отменено' : formatTimeRange(s)}
              </span>
            </span>
            <span className="pc-list-row__aside">{pluralAthletes(s.athleteCount)}</span>
            <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * Соревнования и сборы на «Сегодня» (events.md): только идущие сегодня и
 * начинающиеся в ближайшие 7 дней - ниже тренировок, главный шаг экрана
 * остаётся «открыть занятие». Нет таких - блока нет (ссылка на все события -
 * в быстрых переходах); сбой загрузки виден, а не выдаётся за «событий нет».
 */
function EventsSoon() {
  const { now } = useEnv()
  const navigate = useNavigate()
  const events = useEvents()
  if (events.isPending) return null
  if (events.isError) {
    return (
      <div className="pc-alert" role="alert" style={{ marginTop: 24 }}>
        <span style={{ flex: 1 }}>Соревнования и сборы не загрузились</span>
        <button type="button" className="pc-btn" onClick={() => void events.refetch()}>
          Повторить
        </button>
      </div>
    )
  }
  const soon = eventsSoon(events.data, now())
  if (soon.length === 0) return null
  return (
    <section className="pc-section" aria-labelledby="pc-events-soon">
      <h2 id="pc-events-soon" className="pc-section-title">
        Соревнования и сборы
      </h2>
      <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
        {soon.map((e) => (
          <li key={e.id}>
            <button type="button" className="pc-list-row" onClick={() => navigate(`/schedule/events/${e.id}`)}>
              <span className="pc-list-row__main">
                <span className="pc-list-row__title" style={{ display: 'block' }}>
                  {e.name}
                </span>
                <span className="pc-list-row__meta">
                  {capitalize(relativeStart(e, now()))}
                  {e.start.date > isoDate(now()) ? `, ${formatEventWhen(e, now().getFullYear())}` : ''}
                  {e.start.date === isoDate(now()) && e.start.time ? `, ${e.start.time}` : ''} / {EVENT_KIND_LABEL[e.kind]}
                </span>
              </span>
              {e.participantCount !== undefined && (
                <span className="pc-list-row__aside">{pluralParticipants(e.participantCount)}</span>
              )}
              <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}
